/**
 * Flags panel — list every feature flag loaded on the page, search, and
 * toggle. Toggles dispatch `storeFlags` (session-only override) which is
 * reactive across every connected consumer immediately.
 */
import React, { useMemo, useState } from 'react'
import classnames from 'classnames/bind'
import { Switch } from '@base-ui-components/react/switch'
import { Icon } from '@adc/ui-morpheus'
import { useFlagOverrides } from './useFlagOverrides'
import styles from './styles.scss'

const cx = classnames.bind(styles)

export const FlagsPanel: React.FC = () => {
  const { rows, overriddenCount, toggleFlag, resetFlag, resetAll } =
    useFlagOverrides()
  const [query, setQuery] = useState('')
  const [onlyOverridden, setOnlyOverridden] = useState(false)

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return rows.filter(r => {
      if (onlyOverridden && !r.overridden) return false
      if (!q) return true
      return r.key.toLowerCase().includes(q)
    })
  }, [rows, query, onlyOverridden])

  return (
    <div className={cx('flags-panel')}>
      <div className={cx('flags-toolbar')}>
        <div className={cx('flags-search')}>
          <Icon name='search' className={cx('flags-search-icon')} />
          <input
            className={cx('flags-search-input')}
            placeholder={`Filter ${rows.length} flags…`}
            value={query}
            onChange={e => setQuery(e.target.value)}
            spellCheck={false}
            autoComplete='off'
          />
        </div>
        <button
          type='button'
          className={cx('flags-chip', { active: onlyOverridden })}
          onClick={() => setOnlyOverridden(v => !v)}
          title='Show only overridden flags'
        >
          Overridden {overriddenCount > 0 ? `(${overriddenCount})` : ''}
        </button>
        <button
          type='button'
          className={cx('flags-reset')}
          onClick={resetAll}
          disabled={overriddenCount === 0}
          title='Reset all overrides to server values'
        >
          Reset all
        </button>
      </div>

      <div className={cx('flags-list')}>
        {filtered.length === 0 ? (
          <div className={cx('panel-empty')}>No flags match “{query}”.</div>
        ) : (
          filtered.map(row => (
            <div
              key={row.key}
              className={cx('flag-row', { overridden: row.overridden })}
            >
              <Switch.Root
                checked={row.value}
                onCheckedChange={() => toggleFlag(row.key)}
                className={cx('flag-switch')}
              >
                <Switch.Thumb className={cx('flag-switch-thumb')} />
              </Switch.Root>
              <span className={cx('flag-key')} title={row.key}>
                {row.key}
              </span>
              {row.overridden && (
                <button
                  type='button'
                  className={cx('flag-badge')}
                  onClick={() => resetFlag(row.key)}
                  title={`Server value: ${row.baseline}. Click to reset.`}
                >
                  overridden
                </button>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  )
}
